import { supabase } from "../supabaseClient";

export interface LeagueSeason {
  id: string;
  league_id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  created_at: string;
}

export interface LeaguePlayer {
  id: string;
  league_id: string;
  season_id: string;
  team_id: string;
  player_name: string;
  player_id: string | null;
  role: string;
  batting_style: string;
  bowling_style: string | null;
  jersey_number: number | null;
  is_captain: boolean;
  is_vice_captain: boolean;
  is_wicket_keeper: boolean;
  photo_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeaguePlayerSeasonStats {
  id: string;
  league_id: string;
  season_id: string;
  player_id: string;
  team_id: string;
  player_name: string;
  bat_matches: number;
  bat_innings: number;
  bat_not_outs: number;
  bat_runs: number;
  bat_balls: number;
  bat_highest: number;
  bat_highest_not_out: boolean;
  bat_fours: number;
  bat_sixes: number;
  bat_fifties: number;
  bat_hundreds: number;
  bat_average: number;
  bat_strike_rate: number;
  bowl_innings: number;
  bowl_overs: number;
  bowl_balls: number;
  bowl_maidens: number;
  bowl_runs: number;
  bowl_wickets: number;
  bowl_economy: number;
  bowl_average: number;
  bowl_strike_rate: number;
  bowl_best_wickets: number;
  bowl_best_runs: number;
  field_catches: number;
  field_run_outs: number;
  field_stumpings: number;
  player_of_match: number;
  updated_at: string;
}

export interface LeagueMatchPlayer {
  id: string;
  match_id: string;
  league_id: string;
  season_id: string;
  team_id: string;
  player_id: string;
  player_name: string;
  bat_runs: number;
  bat_balls: number;
  bat_fours: number;
  bat_sixes: number;
  bat_not_out: boolean;
  bat_dismissal: string | null;
  bat_position: number | null;
  bowl_overs: number;
  bowl_balls: number;
  bowl_runs: number;
  bowl_wickets: number;
  bowl_maidens: number;
  field_catches: number;
  field_run_outs: number;
  field_stumpings: number;
  is_player_of_match: boolean;
}

// ===== SEASONS =====

export async function listSeasons(leagueId: string): Promise<LeagueSeason[]> {
  const { data, error } = await supabase
    .from("league_seasons")
    .select("*")
    .eq("league_id", leagueId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[LeagueSeasons] Error fetching seasons:", error);
    return [];
  }
  return data || [];
}

export async function createSeason(leagueId: string, name: string, startDate?: string, endDate?: string): Promise<LeagueSeason | null> {
  const { data, error } = await supabase
    .from("league_seasons")
    .insert({
      league_id: leagueId,
      name,
      start_date: startDate || null,
      end_date: endDate || null,
      is_active: true,
    })
    .select()
    .maybeSingle();

  if (error) {
    console.error("[LeagueSeasons] Error creating season:", error);
    return null;
  }
  return data;
}

export async function updateSeason(seasonId: string, updates: Partial<Pick<LeagueSeason, "name" | "start_date" | "end_date" | "is_active">>): Promise<boolean> {
  const { error } = await supabase
    .from("league_seasons")
    .update(updates)
    .eq("id", seasonId);

  if (error) {
    console.error("[LeagueSeasons] Error updating season:", error);
    return false;
  }
  return true;
}

export async function deleteSeason(seasonId: string): Promise<boolean> {
  const { error } = await supabase
    .from("league_seasons")
    .delete()
    .eq("id", seasonId);

  if (error) {
    console.error("[LeagueSeasons] Error deleting season:", error);
    return false;
  }
  return true;
}

export async function getActiveSeason(leagueId: string): Promise<LeagueSeason | null> {
  const { data, error } = await supabase
    .from("league_seasons")
    .select("*")
    .eq("league_id", leagueId)
    .eq("is_active", true)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("[LeagueSeasons] Error fetching active season:", error);
    return null;
  }
  return data;
}

// ===== PLAYERS =====

export async function listLeaguePlayers(seasonId: string, teamId?: string): Promise<LeaguePlayer[]> {
  let query = supabase
    .from("league_players")
    .select("*")
    .eq("season_id", seasonId)
    .order("player_name", { ascending: true });

  if (teamId) {
    query = query.eq("team_id", teamId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("[LeaguePlayers] Error fetching players:", error);
    return [];
  }
  return data || [];
}

export async function addLeaguePlayer(player: Omit<LeaguePlayer, "id" | "created_at" | "updated_at">): Promise<LeaguePlayer | null> {
  const { data, error } = await supabase
    .from("league_players")
    .insert(player)
    .select()
    .maybeSingle();

  if (error) {
    console.error("[LeaguePlayers] Error adding player:", error);
    return null;
  }
  return data;
}

export async function updateLeaguePlayer(playerId: string, updates: Partial<Omit<LeaguePlayer, "id" | "created_at" | "league_id" | "season_id">>): Promise<boolean> {
  const { error } = await supabase
    .from("league_players")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", playerId);

  if (error) {
    console.error("[LeaguePlayers] Error updating player:", error);
    return false;
  }
  return true;
}

export async function removeLeaguePlayer(playerId: string): Promise<boolean> {
  const { error } = await supabase
    .from("league_players")
    .delete()
    .eq("id", playerId);

  if (error) {
    console.error("[LeaguePlayers] Error removing player:", error);
    return false;
  }
  return true;
}

export async function transferPlayer(playerId: string, newTeamId: string): Promise<boolean> {
  const { error } = await supabase
    .from("league_players")
    .update({ team_id: newTeamId, updated_at: new Date().toISOString() })
    .eq("id", playerId);

  if (error) {
    console.error("[LeaguePlayers] Error transferring player:", error);
    return false;
  }
  return true;
}

// ===== MATCH PLAYERS (per-match performance) =====

export async function saveMatchPlayerPerformance(entries: Omit<LeagueMatchPlayer, "id">[]): Promise<boolean> {
  if (entries.length === 0) return true;

  const { error } = await supabase
    .from("league_match_players")
    .upsert(entries, { onConflict: "match_id,player_id" });

  if (error) {
    console.error("[LeagueMatchPlayers] Error saving match performance:", error);
    return false;
  }
  return true;
}

export async function getMatchPlayers(matchId: string): Promise<LeagueMatchPlayer[]> {
  const { data, error } = await supabase
    .from("league_match_players")
    .select("*")
    .eq("match_id", matchId);

  if (error) {
    console.error("[LeagueMatchPlayers] Error fetching match players:", error);
    return [];
  }
  return data || [];
}

export async function deleteMatchPlayerPerformance(matchId: string): Promise<boolean> {
  const { error } = await supabase
    .from("league_match_players")
    .delete()
    .eq("match_id", matchId);

  if (error) {
    console.error("[LeagueMatchPlayers] Error deleting match performance:", error);
    return false;
  }
  return true;
}

// ===== SEASON STATS =====

export async function getSeasonStats(seasonId: string, teamId?: string): Promise<LeaguePlayerSeasonStats[]> {
  let query = supabase
    .from("league_player_season_stats")
    .select("*")
    .eq("season_id", seasonId);

  if (teamId) {
    query = query.eq("team_id", teamId);
  }

  const { data, error } = await query.order("bat_runs", { ascending: false });

  if (error) {
    console.error("[LeagueStats] Error fetching season stats:", error);
    return [];
  }
  return data || [];
}

export async function recalculateSeasonStats(leagueId: string, seasonId: string): Promise<boolean> {
  const { data: matchPlayers, error } = await supabase
    .from("league_match_players")
    .select("*")
    .eq("league_id", leagueId)
    .eq("season_id", seasonId);

  if (error) {
    console.error("[LeagueStats] Error fetching match players for recalc:", error);
    return false;
  }

  if (!matchPlayers || matchPlayers.length === 0) {
    await supabase
      .from("league_player_season_stats")
      .delete()
      .eq("season_id", seasonId);
    return true;
  }

  const playerMap = new Map<string, {
    player_id: string;
    team_id: string;
    player_name: string;
    matches: Set<string>;
    bat_innings: number;
    bat_not_outs: number;
    bat_runs: number;
    bat_balls: number;
    bat_highest: number;
    bat_highest_not_out: boolean;
    bat_fours: number;
    bat_sixes: number;
    bowl_innings: number;
    bowl_balls: number;
    bowl_maidens: number;
    bowl_runs: number;
    bowl_wickets: number;
    bowl_best_wickets: number;
    bowl_best_runs: number;
    field_catches: number;
    field_run_outs: number;
    field_stumpings: number;
    player_of_match: number;
  }>();

  for (const mp of matchPlayers) {
    const key = mp.player_id;
    if (!playerMap.has(key)) {
      playerMap.set(key, {
        player_id: mp.player_id,
        team_id: mp.team_id,
        player_name: mp.player_name,
        matches: new Set(),
        bat_innings: 0, bat_not_outs: 0, bat_runs: 0, bat_balls: 0,
        bat_highest: 0, bat_highest_not_out: false, bat_fours: 0, bat_sixes: 0,
        bowl_innings: 0, bowl_balls: 0, bowl_maidens: 0, bowl_runs: 0, bowl_wickets: 0,
        bowl_best_wickets: 0, bowl_best_runs: 0,
        field_catches: 0, field_run_outs: 0, field_stumpings: 0,
        player_of_match: 0,
      });
    }
    const s = playerMap.get(key)!;
    s.matches.add(mp.match_id);

    if (mp.bat_runs > 0 || mp.bat_balls > 0 || mp.bat_dismissal) {
      s.bat_innings++;
      s.bat_runs += mp.bat_runs;
      s.bat_balls += mp.bat_balls;
      s.bat_fours += mp.bat_fours;
      s.bat_sixes += mp.bat_sixes;
      if (mp.bat_not_out) s.bat_not_outs++;
      if (mp.bat_runs > s.bat_highest) {
        s.bat_highest = mp.bat_runs;
        s.bat_highest_not_out = mp.bat_not_out;
      }
    }

    if (mp.bowl_balls > 0 || mp.bowl_wickets > 0) {
      s.bowl_innings++;
      s.bowl_balls += mp.bowl_balls;
      s.bowl_maidens += mp.bowl_maidens;
      s.bowl_runs += mp.bowl_runs;
      s.bowl_wickets += mp.bowl_wickets;
      if (mp.bowl_wickets > s.bowl_best_wickets ||
          (mp.bowl_wickets === s.bowl_best_wickets && mp.bowl_runs < s.bowl_best_runs)) {
        s.bowl_best_wickets = mp.bowl_wickets;
        s.bowl_best_runs = mp.bowl_runs;
      }
    }

    s.field_catches += mp.field_catches;
    s.field_run_outs += mp.field_run_outs;
    s.field_stumpings += mp.field_stumpings;
    if (mp.is_player_of_match) s.player_of_match++;
  }

  const statsEntries = Array.from(playerMap.values()).map((s) => {
    const dismissals = s.bat_innings - s.bat_not_outs;
    const batAvg = dismissals > 0 ? s.bat_runs / dismissals : s.bat_runs;
    const batSR = s.bat_balls > 0 ? (s.bat_runs / s.bat_balls) * 100 : 0;
    const bowlOvers = Math.floor(s.bowl_balls / 6) + (s.bowl_balls % 6) / 10;
    const bowlEcon = bowlOvers > 0 ? s.bowl_runs / (s.bowl_balls / 6) : 0;
    const bowlAvg = s.bowl_wickets > 0 ? s.bowl_runs / s.bowl_wickets : 0;
    const bowlSR = s.bowl_wickets > 0 ? s.bowl_balls / s.bowl_wickets : 0;
    const fifties = countMilestones(matchPlayers.filter(mp => mp.player_id === s.player_id), 50);
    const hundreds = countMilestones(matchPlayers.filter(mp => mp.player_id === s.player_id), 100);

    return {
      league_id: leagueId,
      season_id: seasonId,
      player_id: s.player_id,
      team_id: s.team_id,
      player_name: s.player_name,
      bat_matches: s.matches.size,
      bat_innings: s.bat_innings,
      bat_not_outs: s.bat_not_outs,
      bat_runs: s.bat_runs,
      bat_balls: s.bat_balls,
      bat_highest: s.bat_highest,
      bat_highest_not_out: s.bat_highest_not_out,
      bat_fours: s.bat_fours,
      bat_sixes: s.bat_sixes,
      bat_fifties: fifties,
      bat_hundreds: hundreds,
      bat_average: Math.round(batAvg * 100) / 100,
      bat_strike_rate: Math.round(batSR * 100) / 100,
      bowl_innings: s.bowl_innings,
      bowl_overs: bowlOvers,
      bowl_balls: s.bowl_balls,
      bowl_maidens: s.bowl_maidens,
      bowl_runs: s.bowl_runs,
      bowl_wickets: s.bowl_wickets,
      bowl_economy: Math.round(bowlEcon * 100) / 100,
      bowl_average: Math.round(bowlAvg * 100) / 100,
      bowl_strike_rate: Math.round(bowlSR * 100) / 100,
      bowl_best_wickets: s.bowl_best_wickets,
      bowl_best_runs: s.bowl_best_runs,
      field_catches: s.field_catches,
      field_run_outs: s.field_run_outs,
      field_stumpings: s.field_stumpings,
      player_of_match: s.player_of_match,
      updated_at: new Date().toISOString(),
    };
  });

  await supabase
    .from("league_player_season_stats")
    .delete()
    .eq("season_id", seasonId);

  if (statsEntries.length > 0) {
    const { error: insertError } = await supabase
      .from("league_player_season_stats")
      .insert(statsEntries);

    if (insertError) {
      console.error("[LeagueStats] Error inserting season stats:", insertError);
      return false;
    }
  }

  return true;
}

function countMilestones(playerMatches: any[], threshold: number): number {
  return playerMatches.filter(mp => mp.bat_runs >= threshold).length;
}

// ===== CAREER STATS =====

export async function recalculateCareerStats(leagueId: string, playerIds: string[]): Promise<boolean> {
  for (const playerId of playerIds) {
    const { data: allMatchPerf, error } = await supabase
      .from("league_match_players")
      .select("*")
      .eq("league_id", leagueId)
      .eq("player_id", playerId);

    if (error) {
      console.error("[LeagueStats] Error fetching career data:", error);
      continue;
    }

    if (!allMatchPerf || allMatchPerf.length === 0) {
      await supabase
        .from("league_player_career_stats")
        .delete()
        .eq("league_id", leagueId)
        .eq("player_id", playerId);
      continue;
    }

    const matches = new Set(allMatchPerf.map(mp => mp.match_id));
    const seasons = new Set(allMatchPerf.map(mp => mp.season_id));
    let batInnings = 0, batNotOuts = 0, batRuns = 0, batBalls = 0;
    let batHighest = 0, batHighestNO = false, batFours = 0, batSixes = 0;
    let bowlInnings = 0, bowlBalls = 0, bowlMaidens = 0, bowlRuns = 0, bowlWickets = 0;
    let bowlBestW = 0, bowlBestR = 0;
    let catches = 0, runOuts = 0, stumpings = 0, potm = 0;

    for (const mp of allMatchPerf) {
      if (mp.bat_runs > 0 || mp.bat_balls > 0 || mp.bat_dismissal) {
        batInnings++;
        batRuns += mp.bat_runs;
        batBalls += mp.bat_balls;
        batFours += mp.bat_fours;
        batSixes += mp.bat_sixes;
        if (mp.bat_not_out) batNotOuts++;
        if (mp.bat_runs > batHighest) {
          batHighest = mp.bat_runs;
          batHighestNO = mp.bat_not_out;
        }
      }
      if (mp.bowl_balls > 0 || mp.bowl_wickets > 0) {
        bowlInnings++;
        bowlBalls += mp.bowl_balls;
        bowlMaidens += mp.bowl_maidens;
        bowlRuns += mp.bowl_runs;
        bowlWickets += mp.bowl_wickets;
        if (mp.bowl_wickets > bowlBestW || (mp.bowl_wickets === bowlBestW && mp.bowl_runs < bowlBestR)) {
          bowlBestW = mp.bowl_wickets;
          bowlBestR = mp.bowl_runs;
        }
      }
      catches += mp.field_catches;
      runOuts += mp.field_run_outs;
      stumpings += mp.field_stumpings;
      if (mp.is_player_of_match) potm++;
    }

    const dismissals = batInnings - batNotOuts;
    const batAvg = dismissals > 0 ? batRuns / dismissals : batRuns;
    const batSR = batBalls > 0 ? (batRuns / batBalls) * 100 : 0;
    const bowlOvers = Math.floor(bowlBalls / 6) + (bowlBalls % 6) / 10;
    const bowlEcon = bowlBalls > 0 ? bowlRuns / (bowlBalls / 6) : 0;
    const bowlAvg = bowlWickets > 0 ? bowlRuns / bowlWickets : 0;
    const bowlSR = bowlWickets > 0 ? bowlBalls / bowlWickets : 0;
    const fifties = allMatchPerf.filter(mp => mp.bat_runs >= 50).length;
    const hundreds = allMatchPerf.filter(mp => mp.bat_runs >= 100).length;

    const playerName = allMatchPerf[0].player_name;

    await supabase
      .from("league_player_career_stats")
      .upsert({
        league_id: leagueId,
        player_id: playerId,
        player_name: playerName,
        bat_matches: matches.size,
        bat_innings: batInnings,
        bat_not_outs: batNotOuts,
        bat_runs: batRuns,
        bat_balls: batBalls,
        bat_highest: batHighest,
        bat_highest_not_out: batHighestNO,
        bat_fours: batFours,
        bat_sixes: batSixes,
        bat_fifties: fifties,
        bat_hundreds: hundreds,
        bat_average: Math.round(batAvg * 100) / 100,
        bat_strike_rate: Math.round(batSR * 100) / 100,
        bowl_innings: bowlInnings,
        bowl_overs: bowlOvers,
        bowl_balls: bowlBalls,
        bowl_maidens: bowlMaidens,
        bowl_runs: bowlRuns,
        bowl_wickets: bowlWickets,
        bowl_economy: Math.round(bowlEcon * 100) / 100,
        bowl_average: Math.round(bowlAvg * 100) / 100,
        bowl_strike_rate: Math.round(bowlSR * 100) / 100,
        bowl_best_wickets: bowlBestW,
        bowl_best_runs: bowlBestR,
        field_catches: catches,
        field_run_outs: runOuts,
        field_stumpings: stumpings,
        player_of_match: potm,
        seasons_played: seasons.size,
        updated_at: new Date().toISOString(),
      }, { onConflict: "league_id,player_id" });
  }

  return true;
}

export async function getCareerStats(leagueId: string): Promise<LeaguePlayerSeasonStats[]> {
  const { data, error } = await supabase
    .from("league_player_career_stats")
    .select("*")
    .eq("league_id", leagueId)
    .order("bat_runs", { ascending: false });

  if (error) {
    console.error("[LeagueStats] Error fetching career stats:", error);
    return [];
  }
  return data || [];
}
